import React, { useEffect, useState } from 'react';
import Layout from "../layout/Layout";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import CustomPagination from "../components/CustomPagination";
import CustomFormInput from "../components/CustomFormInput";
import DeleteModal from '../components/DeleteModal';
import UserViewModal from '../components/UserManagement/UserViewModal';
import UserEditModal from '../components/UserManagement/UserEditModal';
import { deleteUser, exportUser, getAllUser } from '../controller';
import { requestHandler } from "../helper";
import { formatDateAsItIs } from '../helper/common';
import { accountTypeOf, accountTypeLabel } from '../helper/accountType';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import { Nav, Tab } from 'react-bootstrap';
import ScrollingTab from '../components/ScrollingTab';
import { getThumbnailUrl } from '../helper/image';
import UserActivitiesModal from '../components/UserActivitiesModal';

function UserManagement() {

    const [loading, setLoading] = useState(true);

    const [viewShow, setViewShow] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedActivities, setSelectedActivities] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    const [csvData, setCSVData] = useState([]);

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    const handleViewClose = () => {
        setViewShow(false);
        setSelectedUser(null);
    }
    const handleViewShow = (user) => {
        setSelectedUser(user);
        setViewShow(true);
    }

    const [editShow, setEditShow] = useState(false);
    const [search, setSearch] = useState("");
    // AO 2.0: the tabs are the account type filter (Users = Individual, Organizations = Organization)
    const [activeTab, setActiveTab] = useState("users");
    const isOrgTab = activeTab === "organizations";
    const selectedRole = isOrgTab ? "Organization" : "Individual";
    // "" = All devices, otherwise "android" / "ios"
    const [deviceType, setDeviceType] = useState("");
    const handleEditClose = () => {
        setSelectedUser(null);
        setEditShow(false);
    }
    const handleEditShow = (user) => {
        setEditShow(true);
        setSelectedUser(user);
    }

    const [userData, setUserData] = useState();
    const [paginationValue, setPaginationValue] = useState({
        page: 1,
        limit: 10,
        totalData: 0,
        totalPages: 0
    })

    const { page, limit, totalData, totalPages } = paginationValue
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, totalData);

    const getUserData = async (page = 1) => {
        await requestHandler(
            async () => await getAllUser(limit, page, search, selectedRole || undefined, deviceType || undefined),
            setLoading,
            (res) => {
                const { data, limit, page, totalData, totalPages } = res.data;
                setUserData(data);
                setPaginationValue({
                    page,
                    limit,
                    totalData,
                    totalPages
                })
            },
            () => { }
        );
    };

    useEffect(() => {
        if (search) {
            const delayDebounce = setTimeout(() => {
                getUserData(1);
            }, 500);
            return () => clearTimeout(delayDebounce);
        } else {
            getUserData(1)
        }
        // eslint-disable-next-line
    }, [paginationValue.limit, search, selectedRole, deviceType])

    const deleteUserList = async (id) => {

        await requestHandler(
            async () => await deleteUser(id),
            setLoading,
            (res) => {
                toast.success(res.message);
                getUserData(1);
                setShowDeleteModal(false);
            },
            () => { }
        );
    };


    // Utility to convert array of objects to CSV string with custom headers
    const convertToCSV = (data) => {
        if (!data || !data.length) return '';

        const isOrganizationRow = (row) => accountTypeOf(row.role?.name || row.role?.role_name) === 'organization';
        const hasOrganizations = data.some(isOrganizationRow);
        const hasRegularUsers = data.some(row => !isOrganizationRow(row));

        const processed = data.map((row, idx) => {
            const roleName = row.role?.name || row.role?.role_name || '';
            const isOrganization = isOrganizationRow(row);

            const result = {
                No: idx + 1,
                "Role": accountTypeLabel(roleName),
                "Device Type": row.deviceType
                    ? (row.deviceType.toLowerCase() === 'android'
                        ? 'Android'
                        : row.deviceType.toLowerCase() === 'ios'
                            ? 'iOS'
                            : row.deviceType.charAt(0).toUpperCase() + row.deviceType.slice(1))
                    : '-',
                "Username": row.userName || '',
                "Email Address": row.email || '',
                "Date of Birth": formatDateAsItIs(row.dateOfBirth) || '',
                "Gender": row.gender === 0 ? "Male" : "Female",
                "Bio": row.bio || '',
                "Instagram": row.externalLinks?.instagram || '',
                "TikTok": row.externalLinks?.tiktok || '',
                "YouTube": row.externalLinks?.youtube || '',
                "AllTrails": row.externalLinks?.alltrails || '',
                "onX": row.externalLinks?.onx || '',
                "Website": row.externalLinks?.website || '',
                "Social Media": row.socialMedia || '',
                "Owner Name": [row.owner?.firstName, row.owner?.lastName].filter(Boolean).join(' '),
                "Owner Email": row.owner?.email || '',
                "Owner Date of Birth": row.owner?.dateOfBirth ? formatDateAsItIs(row.owner.dateOfBirth) : '',
                "Joined Date": row.createdAt ? formatDateAsItIs(row.createdAt) : '',
                "Total Posts": row.activity || 0,
                "Total Events": row.event || 0,
                "Total Likes": row.like || 0
            };

            if (isOrganization) {
                result["Organization Name"] = row.firstName || '';
                if (hasRegularUsers) {
                    result["First Name"] = '';
                    result["Last Name"] = '';
                }
            } else {
                result["First Name"] = row.firstName || '';
                result["Last Name"] = row.lastName || '';
                if (hasOrganizations) {
                    result["Organization Name"] = '';
                }
            }

            return result;
        });

        const header = ["No"];
        if (hasOrganizations) {
            header.push("Organization Name");
        }
        if (hasRegularUsers) {
            header.push("First Name", "Last Name");
        }
        header.push("Role", "Device Type", "Username", "Email Address", "Date of Birth", "Gender", "Bio", "Instagram", "TikTok", "YouTube", "AllTrails", "onX", "Website", "Social Media", ...(hasOrganizations ? ["Owner Name", "Owner Email", "Owner Date of Birth"] : []), "Joined Date", "Total Posts", "Total Events", "Total Likes");

        const csvRows = [header.join(",")];
        processed.forEach(row => {
            const values = header.map(field => {
                let val = row[field] !== null && row[field] !== undefined ? row[field] : '';
                if (typeof val === 'string') val = '"' + val.replace(/"/g, '""') + '"';
                return val;
            });
            csvRows.push(values.join(","));
        });
        return csvRows.join("\n");
    };

    const downloadCSV = (csv, filename = 'users.csv') => {
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleExportCSV = async () => {
        await requestHandler(
            async () => await exportUser(search, selectedRole || undefined, deviceType || undefined),
            null,
            (res) => {
                const data = res.data;
                setCSVData(data);
                const csv = convertToCSV(data);
                downloadCSV(csv);
            },
            () => { }
        );
    };

    // Function to handle image click
    const handleImageClick = (activities, user) => {
        if (activities && activities.length > 0) {
            setSelectedActivities(activities);
            setCurrentUser(user);
            setShowImageModal(true);
        }
    };


    return (
        <>
            {loading && <Loader />}
            <Layout>
                <div className="card-wrapper">
                    <div className="card-header">
                        <div className='theme-select'>
                            <Form.Select required name="limit" onChange={e => setPaginationValue({ ...paginationValue, [e.target.name]: e.target.value })}>
                                <option >10</option>
                                <option >15</option>
                                <option >20</option>
                            </Form.Select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div className='theme-select'>
                                <Form.Select name="deviceType" value={deviceType} onChange={e => setDeviceType(e.target.value)}>
                                    <option value="">All Devices</option>
                                    <option value="android">Android</option>
                                    <option value="ios">iOS</option>
                                </Form.Select>
                            </div>
                            <div className="search-input">
                                <img src="/images/svg/search.svg" className="search-icon" alt="icon" />
                                <CustomFormInput
                                    type="search"
                                    placeholder="Search here..."
                                    id="search"
                                    className="mb-0"
                                    value={search}
                                    name='search'
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <div>
                                <button type="button" className="btn btn-primary" disabled={userData?.length === 0} onClick={handleExportCSV}>
                                    <img src={`/images/svg/export-csv.svg`} alt="csv" width={25} height={25} />
                                    Export CSV
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <Tab.Container
                            id="userTab"
                            activeKey={activeTab}
                            onSelect={(key) => {
                                if (!key || key === activeTab) return;
                                setUserData([]);
                                setActiveTab(key);
                                setPaginationValue({ ...paginationValue, page: 1 });
                            }}
                        >
                            <ScrollingTab>
                                <Nav className='theme-tab'>
                                    <Nav.Item>
                                        <Nav.Link eventKey="users">Users</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="organizations">Organizations</Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </ScrollingTab>
                        </Tab.Container>
                        <div className='overflow-x-auto theme-scrollbar mt-3'>
                            <Table className="theme-table">
                                <thead>
                                    <tr>
                                        <th>Username</th>
                                        {isOrgTab && <th>Organization Name</th>}
                                        <th>Device Type</th>
                                        <th>Joined Date</th>
                                        <th>Total Posts</th>
                                        <th>Posts</th>
                                        <th>Total Events</th>
                                        <th>Total Likes</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Boolean(userData?.length) ? userData?.map((user, index) => (
                                        <tr key={index}>
                                            <td>{user.userName}</td>
                                            {isOrgTab && <td>{user.firstName || '-'}</td>}
                                            <td>{
                                                user.deviceType
                                                    ? (user.deviceType.toLowerCase() === 'android'
                                                        ? 'Android'
                                                        : user.deviceType.toLowerCase() === 'ios'
                                                            ? 'iOS'
                                                            : user.deviceType.charAt(0).toUpperCase() + user.deviceType.slice(1))
                                                    : '-'
                                            }</td>
                                            <td>{formatDateAsItIs(user.createdAt)}</td>
                                            <td>{user.activity ? user.activity : 0}</td>
                                            <td>
                                                {(() => {
                                                    const activities = user?.activities || [];
                                                    if (activities.length === 0) {
                                                        return <span>-</span>;
                                                    }

                                                    // Get first activity's first image/video
                                                    const firstActivity = activities[0];
                                                    const firstActivityImages = firstActivity?.activity_image || [];
                                                    if (firstActivityImages.length === 0) {
                                                        return <span>-</span>;
                                                    }

                                                    const firstMedia = firstActivityImages[0];
                                                    const remainingActivitiesCount = activities.length - 1;
                                                    const isVideo = firstMedia?.type && firstMedia.type.startsWith('video/');

                                                    const thumbnailUrl = getThumbnailUrl(firstMedia);

                                                    return thumbnailUrl ? (
                                                        <div
                                                            style={{
                                                                position: 'relative',
                                                                display: 'inline-block',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleImageClick(activities, user)}
                                                        >
                                                            <img
                                                                src={thumbnailUrl}
                                                                alt="Activity"
                                                                style={{
                                                                    width: '80px',
                                                                    height: '80px',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid #ddd'
                                                                }}
                                                            // onError={(e) => {
                                                            //     e.target.onerror = null; // Prevent infinite loop
                                                            //     e.target.style.display = 'none';
                                                            // }}
                                                            />
                                                            {isVideo && (
                                                                <div
                                                                    style={{
                                                                        position: 'absolute',
                                                                        bottom: '4px',
                                                                        left: '4px',
                                                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                                                        borderRadius: '4px',
                                                                        padding: '2px 6px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px'
                                                                    }}
                                                                >
                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                                                        <path d="M8 5v14l11-7z" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            {remainingActivitiesCount > 0 && (
                                                                <span
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: '-8px',
                                                                        right: '-8px',
                                                                        backgroundColor: '#dc3545',
                                                                        color: '#fff',
                                                                        borderRadius: '50%',
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '12px',
                                                                        fontWeight: 'bold',
                                                                        border: '2px solid #fff',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                    }}
                                                                >
                                                                    +{remainingActivitiesCount}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : <span>-</span>;
                                                })()}
                                            </td>
                                            <td>{user.event ? user.event : 0}</td>
                                            <td>{user.like ? user.like : 0}</td>
                                            <td width={135}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <button className="table-icon" onClick={() => handleViewShow(user)}>
                                                        <img src="/images/svg/table-view.svg" alt="icon" />
                                                    </button>
                                                    <button className="table-icon border-danger-light" onClick={() => handleDeleteClick(user)}>
                                                        <img src="/images/svg/table-delete.svg" alt="icon" />
                                                    </button>
                                                    <button className="table-icon border-primary-light" onClick={() => handleEditShow(user)}>
                                                        <img src="/images/svg/table-edit.svg" alt="icon" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) :
                                        <tr>
                                            <td colSpan={13} className='text-center p-5 fs-5'>
                                                No Records Found
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </Table>
                        </div>
                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mt-1">
                            <p className="mb-0 fs-16 fw-500 text-light">
                                Showing {start} to {end} of {totalData} entries
                            </p>
                            {totalPages > 1 && (
                                <CustomPagination
                                    currentPage={page}
                                    totalPages={totalPages}
                                    onPageChange={getUserData}
                                />
                            )}

                        </div>
                    </div>
                </div>
            </Layout>
            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDelete={() => deleteUserList(selectedUser?._id)}
                title="Delete User"
                message="Are you sure you want to delete this user?"
            />
            <UserViewModal show={viewShow} handleClose={handleViewClose} selectedUser={selectedUser} />
            <UserEditModal show={editShow} handleClose={handleEditClose} selectedUser={selectedUser} setLoading={setLoading} getUserData={getUserData} />

            <UserActivitiesModal
                show={showImageModal}
                onClose={() => setShowImageModal(false)}
                activities={selectedActivities}
                user={currentUser}
                onDeleted={() => getUserData(page)}
            />
        </>
    )
}

export default UserManagement